import { rolesControllerFindAll, usersControllerFindAll } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Clock,
  Mail,
  Plus,
  Search,
  Shield,
  SlidersHorizontal,
  UserCheck,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard } from '../../components/shared/stat-card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NormalizedRole {
  id: string;
  name: string;
  licensingAuthorities?: Array<{ authority: string }>;
}

interface UserListItem {
  id: string;
  name: string;
  email: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  roles: Array<{ role?: NormalizedRole } | NormalizedRole>;
}

interface Role {
  id: string;
  name: string;
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/users/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('user:view')) throw redirect({ to: '/dashboard' });
  },
  component: UsersPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeRoles(raw: UserListItem['roles']): NormalizedRole[] {
  return raw
    .map((r) => ('role' in r && r.role ? r.role : (r as NormalizedRole)))
    .filter((r): r is NormalizedRole => !!(r as NormalizedRole).id);
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

// ── Role badges ───────────────────────────────────────────────────────────────

const ROLE_COLOR_MAP: Record<string, string> = {
  admin: 'border-violet-200 bg-violet-50 text-violet-700',
  permits: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  payments: 'border-blue-200 bg-blue-50 text-blue-700',
  customer: 'border-amber-200 bg-amber-50 text-amber-700',
  viewer: 'border-gray-200 bg-gray-100 text-gray-600',
};

function getRoleColor(name: string): string {
  const key = name.toLowerCase();
  for (const [k, v] of Object.entries(ROLE_COLOR_MAP)) {
    if (key.includes(k)) return v;
  }
  return 'border-gray-200 bg-gray-100 text-gray-600';
}

function RoleBadge({ name }: { name: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
        getRoleColor(name),
      )}
    >
      {name}
    </span>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'INVITED';

const STATUS_CONFIG: Record<UserStatus | string, { label: string; dot: string; cls: string }> = {
  ACTIVE: {
    label: 'Active',
    dot: 'bg-success-500',
    cls: 'border-success-200 bg-success-50 text-success-600',
  },
  INACTIVE: {
    label: 'Inactive',
    dot: 'bg-gray-400',
    cls: 'border-gray-200 bg-gray-50 text-gray-500',
  },
  SUSPENDED: {
    label: 'Suspended',
    dot: 'bg-red-500',
    cls: 'border-red-200 bg-red-50 text-red-600',
  },
  INVITED: {
    label: 'Invited',
    dot: 'bg-blue-400',
    cls: 'border-blue-200 bg-blue-50 text-blue-600',
  },
};

function UserStatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.INACTIVE;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        cfg.cls,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

// ── Stat cards ────────────────────────────────────────────────────────────────

const FILTER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INVITED', label: 'Invited' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

// ── List table columns ────────────────────────────────────────────────────────

const USER_COLUMNS: CardDataTableColumn[] = [
  { id: 'user', header: 'User' },
  { id: 'roles', header: 'Roles' },
  { id: 'status', header: 'Status' },
  { id: 'lastLogin', header: 'Last Login' },
];

// grid: user-info | roles | status | lastLogin
const USER_GRID = 'minmax(160px, 1.5fr) minmax(160px, 1.5fr) 140px minmax(110px, 1fr)';

// ── Page ──────────────────────────────────────────────────────────────────────

function UsersPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const licensingAuthorities = useAuthStore((s) => s.licensingAuthorities);
  const showAuthorityFilter = licensingAuthorities.length !== 1;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [roleFilter, setRoleFilter] = useState('__all__');
  const [authorityFilter, setAuthorityFilter] = useState('__all__');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, roleFilter, authorityFilter]);

  const {
    data: rawUsers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersControllerFindAll() as unknown as Promise<UserListItem[]>,
  });

  const { data: allRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesControllerFindAll() as unknown as Promise<Role[]>,
  });

  const users = rawUsers ?? [];

  // Stats
  const totalCount = users.length;
  const activeCount = users.filter((u) => u.status === 'ACTIVE').length;
  const adminCount = users.filter((u) =>
    normalizeRoles(u.roles).some((r) => r.name.toLowerCase().includes('admin')),
  ).length;
  const pendingCount = users.filter(
    (u) => u.status === 'INACTIVE' || u.status === 'INVITED',
  ).length;

  // Filtered list
  const filtered = users.filter((u) => {
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== '__all__' && u.status !== statusFilter) return false;
    if (roleFilter !== '__all__') {
      const roles = normalizeRoles(u.roles);
      if (!roles.some((r) => r.id === roleFilter)) return false;
    }
    if (authorityFilter !== '__all__') {
      const roles = normalizeRoles(u.roles);
      const userAuthorities = roles.flatMap(
        (r) => r.licensingAuthorities?.map((la) => la.authority) ?? [],
      );
      if (!userAuthorities.includes(authorityFilter)) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const roleOptions = [
    { value: '__all__', label: 'All Roles' },
    ...(allRoles ?? []).map((r) => ({ value: r.id, label: r.name })),
  ];

  const authorityOptions = [
    { value: '__all__', label: 'All Authorities' },
    { value: 'KALBA_MUNICIPALITY', label: 'Kalba Municipality' },
    { value: 'SHARJAH_MUNICIPALITY', label: 'Sharjah Municipality' },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Users"
        description="Manage system users, roles, and access permissions."
        actions={
          can('user:create') ? (
            <Button variant="success" onClick={() => navigate({ to: '/users/new' })}>
              <Plus size={16} />
              Create New User
            </Button>
          ) : null
        }
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Users size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          label="Total Users"
          value={totalCount}
          delta="+2 this month"
          loading={isLoading}
        />
        <StatCard
          icon={<UserCheck size={18} />}
          iconBg="bg-success-50"
          iconColor="text-success-600"
          label="Active Users"
          value={activeCount}
          delta="Currently online"
          loading={isLoading}
        />
        <StatCard
          icon={<Shield size={18} />}
          iconBg="bg-violet-50"
          iconColor="text-violet-600"
          label="Admin Users"
          value={adminCount}
          delta="Full access"
          loading={isLoading}
        />
        <StatCard
          icon={<Mail size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          label="Pending Invites"
          value={pendingCount}
          delta="Awaiting signup"
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        {/* Search */}
        <div className="relative min-w-60 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or department…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {FILTER_PILLS.map((pill) => (
            <button
              key={pill.value}
              type="button"
              onClick={() => setStatusFilter(pill.value)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                statusFilter === pill.value
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* Roles dropdown */}
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="h-9 w-40 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roleOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Licensing Authority dropdown — hidden for single-authority actors, who only ever see their own municipality anyway */}
        {showAuthorityFilter && (
          <Select value={authorityFilter} onValueChange={setAuthorityFilter}>
            <SelectTrigger className="h-9 w-48 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {authorityOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Sort (decorative) */}
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-500 font-medium hover:bg-gray-50 transition-colors"
        >
          <SlidersHorizontal size={13} />
          Sort
        </button>

        {/* Count */}
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {totalCount} users
        </span>
      </div>

      {/* User list */}
      <CardDataTable
        gridTemplateColumns={USER_GRID}
        columns={USER_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load users</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <Users size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No users found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your search or filters</p>
          </div>
        }
        rows={paginated.map((user): CardDataTableRowDef => {
          const roles = normalizeRoles(user.roles);
          return {
            id: user.id,
            avatar: <GradientAvatar name={user.name} size="md" shape="circle" />,
            cells: {
              user: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors leading-snug">
                    {user.name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-gray-400">{user.email}</p>
                </div>
              ),
              roles: (
                <div className="flex flex-wrap items-center gap-1.5">
                  {roles.length > 0 ? (
                    <>
                      {roles.slice(0, 2).map((r) => (
                        <RoleBadge key={r.id} name={r.name} />
                      ))}
                      {roles.length > 2 && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                          +{roles.length - 2}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">No roles</span>
                  )}
                </div>
              ),
              status: <UserStatusBadge status={user.status} />,
              lastLogin: (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock size={12} className="shrink-0 text-gray-400" />
                  {user.status === 'INACTIVE' || user.status === 'INVITED'
                    ? 'Never'
                    : formatRelative(user.updatedAt)}
                </span>
              ),
            },
            onClick: () => navigate({ to: '/users/$userId', params: { userId: user.id } }),
          };
        })}
      />

      {/* Pagination footer */}
      {!isLoading && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {paginated.length} of {filtered.length} users
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
